import { computed, inject, Injectable, InjectionToken, signal } from '@angular/core';
import {
  DEFAULT_NOTIFICATION_TONE,
  isNotificationToneId,
  notificationTone,
  NotificationToneId,
} from 'app/shared/utils/notification-tones';

/** Hands out the page's audio context, or null where the browser has none. */
export const NOTIFICATION_AUDIO = new InjectionToken<() => AudioContext | null>(
  'NOTIFICATION_AUDIO',
  {
    providedIn: 'root',
    factory: () => {
      let context: AudioContext | null = null;
      return () => {
        if (!context && typeof AudioContext !== 'undefined') {
          context = new AudioContext();
        }
        return context;
      };
    },
  }
);

export interface NotificationSoundPrefs {
  enabled: boolean;
  tone: NotificationToneId;
}

const STORAGE_KEY = 'pc_notification_sound';
/** Ids some tab of this browser already rang for, shared so only one tab rings. */
const HEARD_KEY = 'pc_notification_sound_heard';
/** Enough to cover several polls' worth of arrivals; older ids never come back as new. */
const HEARD_LIMIT = 200;

const DEFAULTS: NotificationSoundPrefs = { enabled: true, tone: DEFAULT_NOTIFICATION_TONE };

/** localStorage, or null where there is none (tests without DOM). */
function browserStorage(): Storage | null {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

/** What this browser saved, with the defaults for anything missing or unknown. */
function readStoredPrefs(): NotificationSoundPrefs {
  try {
    const raw = JSON.parse(browserStorage()?.getItem(STORAGE_KEY) ?? 'null');
    return {
      enabled: typeof raw?.enabled === 'boolean' ? raw.enabled : DEFAULTS.enabled,
      tone: isNotificationToneId(raw?.tone) ? raw.tone : DEFAULTS.tone,
    };
  } catch {
    // Unreadable storage behaves like a browser that never saved anything.
    return { ...DEFAULTS };
  }
}

function readHeard(): string[] {
  try {
    const raw = JSON.parse(browserStorage()?.getItem(HEARD_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * The bell's sound: on by default, kept per browser (like the date formats), so
 * muting the office computer leaves the laptop ringing.
 *
 * Two ways in, one setting:
 * - the speaker in the bell panel (`setEnabled`) saves at once;
 * - Profile → Preferences edits a draft (`apply`), saved with the page's bar
 *   (`save`) or thrown away (`restoreSaved`), like every other preference there.
 *
 * Browsers refuse to play audio before the user has clicked the page; by the
 * time a poll brings something new that click has normally happened, and if it
 * has not the sound is simply skipped.
 */
@Injectable({ providedIn: 'root' })
export class NotificationSoundService {
  private readonly _audio = inject(NOTIFICATION_AUDIO);

  private readonly _saved = signal<NotificationSoundPrefs>(readStoredPrefs());
  private readonly _draft = signal<NotificationSoundPrefs>(this._saved());

  /** Whether the bell rings, as saved. */
  readonly savedEnabled = computed(() => this._saved().enabled);
  /** The tone the bell rings with, as saved. */
  readonly savedTone = computed(() => this._saved().tone);

  /** The switch as Preferences shows it (the draft). */
  readonly enabled = computed(() => this._draft().enabled);
  /** The tone as Preferences shows it (the draft). */
  readonly tone = computed(() => this._draft().tone);

  /** How many of the two settings the draft changed. */
  readonly pendingCount = computed(() => {
    const draft = this._draft();
    const saved = this._saved();
    return (draft.enabled !== saved.enabled ? 1 : 0) + (draft.tone !== saved.tone ? 1 : 0);
  });

  readonly hasUnsavedChanges = computed(() => this.pendingCount() > 0);

  /** Preferences: change the draft without saving. */
  apply(patch: Partial<NotificationSoundPrefs>): void {
    this._draft.update((draft) => ({ ...draft, ...patch }));
  }

  /**
   * Persist the draft.
   * @returns false when storage refused the write; the draft then stays pending.
   */
  save(): boolean {
    return this._persist(this._draft());
  }

  /** Throw the draft away. */
  restoreSaved(): void {
    this._draft.set(this._saved());
  }

  /**
   * The bell's speaker: turns the sound on or off and saves at once. A draft open
   * in Preferences follows, so the two never disagree.
   */
  setEnabled(enabled: boolean): void {
    if (this._persist({ ...this._saved(), enabled })) {
      this._draft.update((draft) => ({ ...draft, enabled }));
    }
  }

  /** Plays a tone so the user can hear it, whether the sound is on or not. */
  preview(tone: NotificationToneId): void {
    this._play(tone);
  }

  /**
   * Rings for newly arrived notifications, once: not while the sound is off, and
   * not for ids another tab of this browser already rang for.
   * @returns whether it rang.
   */
  announce(ids: string[]): boolean {
    if (!this.savedEnabled() || ids.length === 0) {
      return false;
    }
    const heard = readHeard();
    const unheard = ids.filter((id) => !heard.includes(id));
    if (unheard.length === 0) {
      return false;
    }
    try {
      browserStorage()?.setItem(
        HEARD_KEY,
        JSON.stringify([...heard, ...unheard].slice(-HEARD_LIMIT))
      );
    } catch {
      // Without shared storage each tab rings on its own; still better than silence.
    }
    return this._play(this.savedTone());
  }

  private _persist(prefs: NotificationSoundPrefs): boolean {
    try {
      browserStorage()?.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      return false;
    }
    this._saved.set(prefs);
    return true;
  }

  private _play(id: NotificationToneId): boolean {
    const context = this._audio();
    if (!context) {
      return false;
    }
    if (context.state === 'suspended') {
      // Rejected until the user's first click on the page; the sound is skipped.
      context.resume().catch(() => undefined);
    }
    notificationTone(id).play(context, context.destination, context.currentTime + 0.03);
    return true;
  }
}
